import { Button, Chip, Link } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { convertToDateMX } from 'utils/helpers/convertToDateMX';
import { REGEX } from 'utils/regex';

const SEARCH_NAV_STATE_STORAGE_KEY = 'lineAssignments:searchNavigationState';

const getPersistedSearchNavigationState = () => {
  try {
    const persistedState = sessionStorage.getItem(SEARCH_NAV_STATE_STORAGE_KEY);
    return persistedState ? JSON.parse(persistedState) : {};
  } catch {
    return {};
  }
};

export const useSearchOrder = ({
  ordersAssignments,
  getOrdersAssignments,
  cleanOrdersAssignments,
  createOrderLineAssignment,
  createOrderLineAssignmentAction,
  cleanCreateOrderLineAssignment,
}) => {
  const { payload } = ordersAssignments || {};
  const { linked_up_orders } = payload || {};

  const location = useLocation();
  const navigate = useNavigate();
  const navigationState = useMemo(() => {
    if (location.state && Object.keys(location.state).length > 0) {
      return location.state;
    }

    return getPersistedSearchNavigationState();
  }, [location.state]);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({});
  const [searchFormData, setSearchFormData] = useState({});
  const [shouldSearch, setShouldSearch] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogTitleMessage, setDialogTitleMessage] = useState('');
  const [rfc, setRfc] = useState('');
  const [supplier, setSupplier] = useState('');
  const [mappedRows, setMappedRows] = useState([]);
  const page = 0;
  const size = 1;

  // Filtros locales
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [orderIdFilter, setOrderIdFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  // Opciones de status
  const statusOptions = [
    { value: '', label: 'Todas' },
    { value: '1', label: 'Abierta' },
    { value: '2', label: 'En proceso' },
    { value: '3', label: 'Completada Exitosa' },
    { value: '4', label: 'Completada Fallida' },
  ];

  const taxDataValidations = {
    rfc: {
      required: 'El RFC es requerido',
      pattern: {
        value: REGEX.rfc,
        message: 'El RFC debe tener 12 o 13 caracteres',
      },
    },
    carrier_id: {
      required: 'El proveedor es requerido',
    },
  };

  //useForm
  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();

  const Columns = [
    {
      field: 'order_id',
      headerName: 'No. de orden',
      width: 220,
      align: 'center',
      headerAlign: 'center',
      //viculo que ejecute handleRowClick
      renderCell: (params) => (
        <Link
          sx={{
            color: '#1976d2',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
          href='#'
          onClick={(e) => {
            e.preventDefault();
            navigateToOrderPage(true, params.row.order_id);
          }}
        >
          {params.row.order_id}
        </Link>
      ),
    },
    {
      field: 'created',
      headerName: 'Fecha de registro',
      width: 330,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (params) => {
        return convertToDateMX(params);
      },
    },
    {
      field: 'lines_count',
      headerName: 'Líneas ',
      width: 250,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'status',
      headerName: 'Estatus',
      width: 280,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={translationMap[params.row.order_status_id] || params.row.order_status_id}
          style={{
            backgroundColor: getStatusColor(params.row.status),
            color: 'white',
          }}
        />
      ),
    },
    {
      field: 'order_pdf_url',
      headerName: 'Acuse',
      width: 250,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) =>
        params.row.order_pdf_url ? (
          <Button
            variant='outlined'
            onClick={() => window.open(params.row.order_pdf_url, '_blank')}
          >
            {'Descargar'}
          </Button>
        ) : (
          ''
        ),
    },
  ];

  const getStatusColor = (paymentStatus) => {
    switch (paymentStatus) {
      case 'OPEN':
        return 'green';
      case 'IN PROGRESS':
        return 'orange';
      case 'SUCCESSFULLY COMPLETED':
        return 'green';
      case 'FAILED COMPLETED':
        return 'red';
      default:
        return 'purple';
    }
  };

  const translationMap = {
    1: 'Abierta',
    2: 'En proceso',
    3: 'Completada Exitosa',
    4: 'Completada Fallida',
  };

  // Filtrado local
  const filteredRows = useMemo(() => {
    let rows = mappedRows;
    // Status
    if (statusFilter) {
      rows = rows.filter((row) => String(row.order_status_id) === statusFilter);
    }
    // Order ID
    if (orderIdFilter) {
      rows = rows.filter(
        (row) =>
          row.order_id && String(row.order_id).toLowerCase().includes(orderIdFilter.toLowerCase()),
      );
    }
    // Fecha
    if (dateFilter) {
      rows = rows.filter((row) => {
        // created puede ser Date o string, se asume string
        return row.created && row.created.toLowerCase().includes(dateFilter.toLowerCase());
      });
    }
    return rows;
  }, [mappedRows, statusFilter, orderIdFilter, dateFilter]);

  const handleClose = () => {
    setIsDialogOpen(false);
    setDialogMessage('');
    setDialogTitleMessage('');
    cleanOrdersAssignments();
    cleanCreateOrderLineAssignment();
  };

  const onSubmit = (data) => {
    setMappedRows([]);
    // Reinicia todos los filtros locales para evitar arrastrar filtros entre búsquedas RFC.
    setStatusFilter('');
    setOrderIdFilter('');
    setDateFilter('');

    setSearchFormData(data);
    sessionStorage.setItem(
      SEARCH_NAV_STATE_STORAGE_KEY,
      JSON.stringify({
        searchFormData: data,
      }),
    );
    setRfc(data.legal_id || '');
    const supplierName = data.carrier_id === 1 ? 'Dalefon' : data.carrier_id === 2 ? 'IPB' : '';
    setSupplier(supplierName);
    const filteredData = Object.fromEntries(
      Object.entries(data).filter(([, value]) => value !== ''),
    );
    setSearchParams(filteredData);
    setShouldSearch(true);
  };

  const createOrder = () => {
    setLoading(true);
    setIsCreatingOrder(true);
    createOrderLineAssignmentAction(searchFormData);
  };

  const navigateToOrderPage = useCallback(
    (isProcessOrder, orderId) => {
      const dataToPersist = Object.keys(searchFormData).length > 0 ? searchFormData : getValues();

      sessionStorage.setItem(
        SEARCH_NAV_STATE_STORAGE_KEY,
        JSON.stringify({
          searchFormData: dataToPersist,
        }),
      );

      navigate('/line-assignments/order-page', {
        state: {
          searchFormData: dataToPersist,
          isProcessOrder: isProcessOrder || false,
          order_id: orderId || null,
        },
      });
      cleanOrdersAssignments();
      cleanCreateOrderLineAssignment();
    },
    [searchFormData, getValues, navigate, cleanOrdersAssignments, cleanCreateOrderLineAssignment],
  );

  useEffect(() => {
    if (!location.state || Object.keys(location.state).length === 0) {
      return;
    }

    sessionStorage.setItem(SEARCH_NAV_STATE_STORAGE_KEY, JSON.stringify(location.state));
  }, [location.state]);

  useEffect(() => {
    //setear valores de location.state?.searchFormData en el formulario y hacer la búsqueda si es que existen
    const searchFormDataFromState = navigationState?.searchFormData;
    if (searchFormDataFromState) {
      setSearchFormData(searchFormDataFromState);
      setRfc(searchFormDataFromState.legal_id || '');
      const supplierName =
        searchFormDataFromState.carrier_id === 1
          ? 'Dalefon'
          : searchFormDataFromState.carrier_id === 2
            ? 'IPB'
            : '';
      setSupplier(supplierName);
      const filteredData = Object.fromEntries(
        Object.entries(searchFormDataFromState).filter(([, value]) => value !== ''),
      );
      setSearchParams(filteredData);
      setValue('legal_id', searchFormDataFromState.legal_id || '');
      setValue('carrier_id', searchFormDataFromState.carrier_id || '');
      setShouldSearch(true);
    } else {
      setShouldSearch(false);
    }
  }, [navigationState, setValue]);

  useEffect(() => {
    if (!isCreatingOrder) {
      return;
    }

    if (createOrderLineAssignment?.status === 200) {
      setLoading(false);
      setIsCreatingOrder(false);
      navigateToOrderPage(false, createOrderLineAssignment?.payload?.order_id);
      cleanCreateOrderLineAssignment();
    } else if (createOrderLineAssignment?.payload?.response?.status > 200) {
      setDialogTitleMessage(createOrderLineAssignment?.payload?.response?.data?.title);
      setDialogMessage(
        createOrderLineAssignment?.payload?.response?.data?.detail ?? 'Ocurrió un error.',
      );
      setIsDialogOpen(true);
      setLoading(false);
      setIsCreatingOrder(false);
      cleanCreateOrderLineAssignment();
    }
  }, [
    createOrderLineAssignment,
    isCreatingOrder,
    cleanCreateOrderLineAssignment,
    navigateToOrderPage,
  ]);

  useEffect(() => {
    if (shouldSearch) {
      setLoading(true);
      const paramsToSend = Object.keys(searchParams).length !== 0 ? searchParams : undefined;

      getOrdersAssignments(paramsToSend)
        .then(() => setLoading(false))
        .catch(() => {
          setLoading(false);
        });
    }
    return () => {
      cleanOrdersAssignments();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, getOrdersAssignments, cleanOrdersAssignments]);

  useEffect(() => {
    if (ordersAssignments?.status === 200) {
      setLoading(false);
    } else if (ordersAssignments?.payload?.response?.status > 200) {
      setDialogTitleMessage(ordersAssignments?.payload?.response?.data?.title);
      setDialogMessage(ordersAssignments?.payload?.response?.data?.detail ?? 'Ocurrió un error.');
      setIsDialogOpen(true);
      setLoading(false);
    }
  }, [ordersAssignments]);

  useEffect(() => {
    if (linked_up_orders) {
      const updatedRows = linked_up_orders.map((row) => ({
        ...row,
        id: `${row.order_id}`,
      }));
      setMappedRows(updatedRows);
    }
  }, [linked_up_orders]);

  return {
    errors,
    handleSubmit,
    loading,
    onSubmit,
    register,
    control,
    mappedRows,
    Columns,
    isDialogOpen,
    dialogMessage,
    dialogTitleMessage,
    handleClose,
    shouldSearch,
    rfc,
    supplier,
    navigateToOrderPage,
    createOrder,
    page,
    size,
    filteredRows,
    statusOptions,
    statusFilter,
    setStatusFilter,
    orderIdFilter,
    setOrderIdFilter,
    dateFilter,
    setDateFilter,
    taxDataValidations,
    setValue,
  };
};
